import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { BsExclamationTriangle } from '@react-icons/all-files/bs/BsExclamationTriangle';
import { BsInboxFill } from '@react-icons/all-files/bs/BsInboxFill';
import { FormFeedback, Input as BootstrapInput, Label } from 'reactstrap';
import { bind } from '../Utils/Bind';
import { componentAutoReaction } from '../Utils/MobxTools';
import { IFormField } from '../Utils/PrimitiveFormField';

export interface IInputProps<T> {
    customMapper?(
        value: string,
        values?: (string | number)[]
    ): string | number | boolean | string[] | T | T[] | undefined;
    onChange?(): void;
    innerRef?(ref: HTMLInputElement | null): void;
    readonly field: IFormField<string | number | boolean | (string | number)[] | T | T[] | FileList | undefined>;
    readonly value?: string | number | (string | number)[] | boolean | undefined;
    readonly validation?: string;
    readonly className?: string;
    readonly disabled?: boolean;
    readonly required?: boolean;
    readonly path?: string;
    readonly id?: string;
    readonly placeholder?: string;
    readonly regex?: string;
    readonly type:
    | 'text'
    | 'email'
    | 'select'
    | 'file'
    | 'radio'
    | 'checkbox'
    | 'textarea'
    | 'button'
    | 'reset'
    | 'submit'
    | 'date'
    | 'datetime-local'
    | 'hidden'
    | 'image'
    | 'month'
    | 'number'
    | 'range'
    | 'search'
    | 'tel'
    | 'url'
    | 'week'
    | 'password'
    | 'datetime'
    | 'time'
    | 'color';
    readonly min?: number;
    readonly max?: number;
    readonly step?: number;
    readonly trim?: boolean;
    readonly checked?: boolean;
    readonly forceToString?: boolean;
    readonly readOnly?: boolean;
    readonly multiple?: boolean;
}

@observer
export class Input<T> extends React.Component<IInputProps<T>> {
    public constructor(props: IInputProps<T>) {
        super(props);

        componentAutoReaction(
            this,
            () => this.validations,
            v => props.field.onMount(v)
        );
    }

    public render() {
        if (this.props.type == 'file' && !this.props.id) throw new Error('A unique id must be specified for type file');

        return (
            <span>
                <BootstrapInput valid={this.validState} invalid={this.invalidState} {...this.formControlProps}>
                    {this.props.children}
                </BootstrapInput>
                {this.props.field.submitted &&
                    this.props.field.errors?.map((e, idx) => (
                        <FormFeedback key={idx} valid={this.validState}>
                            {e}
                        </FormFeedback>
                    ))}

                {this.props.type == 'file' && (
                    <Label for={this.props.id} className={this.fileInputClass}>
                        <em>{this.props.field.errorState == 'danger' ? <BsExclamationTriangle /> : <BsInboxFill />}</em>{' '}
                        {this.fileImportText}
                    </Label>
                )}
            </span>
        );
    }

    @bind
    @action
    private onFieldChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;

        switch (this.props.type) {
            case 'number':
                if (!isNaN(target.valueAsNumber)) {
                    this.props.field.onChange(target.valueAsNumber);
                } else {
                    this.props.field.onChange(undefined);
                }
                break;
            case 'checkbox':
                this.props.field.onChange(target.checked);
                break;

            case 'select':
            case 'radio':
                if (target.value == '') {
                    this.props.field.onChange(undefined);
                } else if (this.props.customMapper) {
                    const selected = this.getValues(event.target as HTMLSelectElement);
                    this.props.field.onChange(this.props.customMapper(target.value, selected));
                } else if (this.props.multiple) {
                    const selected = this.getValues(event.target as HTMLSelectElement);
                    this.props.field.onChange(selected);
                } else {
                    const value = Number(target.value);
                    if (!this.props.forceToString && !isNaN(value)) {
                        this.props.field.onChange(value);
                    } else {
                        this.props.field.onChange(target.value);
                    }
                }

                break;

            //case "date":
            //    value = target.valueAsDate;
            //    break;
            case 'file':
                if (target.files) {
                    this.fileName = target.value;
                    this.props.field.onChange(target.files);
                }
                break;
            default:
                this.props.field.onChange(target.value);
                break;
        }

        if (this.props.onChange) this.props.onChange();
    }

    @bind
    private onBlur(event: React.SyntheticEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        switch (this.props.type) {
            case 'text':
            case 'textarea':
            case 'email':
                if (this.props.trim != false) this.props.field.onChange(target.value.trim() || undefined);
                break;
        }
    }

    @computed
    private get validations() {
        const rules = new Set<string>(this.props.validation?.split('|'));
        if (this.props.required && this.props.type == 'checkbox') rules.add('accepted');
        if (this.props.required) rules.add('required');
        if (this.props.required && this.props.multiple) rules.add('required:min:1');
        if (this.props.type == 'email') rules.add('email');
        if (this.props.min) rules.add(`min:${this.props.min}`);
        if (this.props.max) rules.add(`max:${this.props.max}`);
        if (this.props.regex) rules.add(`regex:'${this.props.regex}'`);

        return Array.from(rules).join('|');
    }

    private getValues(target: HTMLSelectElement) {
        if (!this.props.multiple) return undefined;

        return [...target.selectedOptions].map((o: HTMLOptionElement) => {
            const value = Number(o.value);
            return !this.props.forceToString && !isNaN(value) ? value : o.value;
        });
    }

    @computed
    private get formControlProps() {
        const props: any = {
            className: this.props.className,
            disabled: this.props.disabled,
            id: this.props.id,
            placeholder: this.props.placeholder,
            type: this.props.type,
            state: this.props.field.state,
            onChange: this.onFieldChange,
            onBlur: this.onBlur,
            step: this.props.step,
            //state: this.props.checked ? 'checked' : null,
            readOnly: this.props.readOnly,
            multiple: this.props.multiple,
            innerRef: this.props.innerRef,
        };

        switch (this.props.type) {
            case 'checkbox':
                props.checked = this.props.field.value;
                break;
            case 'radio':
                props.checked = this.props.field.value == this.props.value;
                props.value = this.props.value;
                break;
            case 'file':
                props.value = this.fileName;
                props.className = 'd-none';
                break;
            default:
                if (this.props.value != undefined && this.props.value != null) {
                    props.value = this.props.value;
                } else if (this.props.field.value != undefined && this.props.field.value != null) {
                    props.value = this.props.field.value;
                } else {
                    props.value = '';
                }
                break;
        }

        return props;
    }

    @computed
    private get validState() {
        switch (this.props.field.state) {
            default:
                return undefined;
            case 'danger':
                return false;
            case 'success':
                return true;
        }
    }

    @computed
    private get invalidState() {
        switch (this.props.field.state) {
            default:
                return undefined;
            case 'danger':
                return true;
            case 'success':
                return false;
        }
    }

    @computed
    private get fileImportText() {
        const fileList = this.props.field.value as FileList | null | undefined;

        if (fileList?.length) {
            return fileList.length + ' files selected';
        } else if (fileList?.length == 1) {
            return fileList[0].name;
        } else {
            return 'Choose A File...';
        }
    }

    @computed
    private get fileInputClass() {
        return `btn btn-${this.props.field.errorState ?? 'link'} ${this.props.className} truncate mb-0`;
    }

    @observable
    private fileName: string = '';
}
